//src/pages/questions/CreateAssessmentPage2.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaInfoCircle } from "react-icons/fa";
import { IoMdArrowRoundBack } from "react-icons/io";

const CreateAssessmentPage2 = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { difficulty, testType, sections } = state;

  const [questionCounts, setQuestionCounts] = useState({
    conversational: 0,
    subjective: 0,
    objective: 0,
  });

  const [sectionMarks, setSectionMarks] = useState({
    conversational: 0,
    subjective: 0,
    objective: 0,
  });

  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [subjectiveTooltip, setSubjectiveTooltip] = useState(false);
  const [objectiveTooltip, setObjectiveTooltip] = useState(false);
  const [aiTooltip, setAiTooltip] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Validate question counts
  useEffect(() => {
    const isValidCount = (count) => count >= 3 && count <= 20;
    const isValidMarks = (marks, count) => marks > 0 && marks % count === 0;
    let formError = "";
    const isFormComplete =
      (!sections.conversational_ai ||
        (isValidCount(questionCounts.conversational) &&
          isValidMarks(
            sectionMarks.conversational,
            questionCounts.conversational
          ))) &&
      (!sections.subjective ||
        (isValidCount(questionCounts.subjective) &&
          isValidMarks(sectionMarks.subjective, questionCounts.subjective))) &&
      (!sections.objective ||
        (isValidCount(questionCounts.objective) &&
          isValidMarks(sectionMarks.objective, questionCounts.objective)));

    if (!isFormComplete) {
      if (
        sections.conversational_ai &&
        !isValidMarks(
          sectionMarks.conversational,
          questionCounts.conversational
        )
      ) {
        formError =
          "Total Marks for Conversational AI should be a multiple of the number of questions selected.";
      } else if (
        sections.subjective &&
        !isValidMarks(sectionMarks.subjective, questionCounts.subjective)
      ) {
        formError =
          "Total Marks for Subjective Questions should be a multiple of the number of questions selected.";
      } else if (
        sections.objective &&
        !isValidMarks(sectionMarks.objective, questionCounts.objective)
      ) {
        formError =
          "Total Marks for Objective Questions should be a multiple of the number of questions selected.";
      } else {
        formError = "Please fill out all required fields.";
      }
    }
    setIsNextDisabled(!isFormComplete);
    setErrorMessage(formError);
  }, [questionCounts, sectionMarks, sections]);

  const calculateTotalTime = () => {
    const { conversational, subjective, objective } = questionCounts;
    return conversational * 3 + subjective * 3 + objective * 0.5;
  };

  const handleNext = () => {
    const totalTime = calculateTotalTime();
    navigate(`/create-assessment/step3/${projectId}`, {
      state: {
        difficulty,
        testType,
        sections,
        questionCounts,
        totalTime,
        sectionMarks,
        marksPerQuestion: {
          conversational:
            sectionMarks.conversational / questionCounts.conversational,
          subjective: sectionMarks.subjective / questionCounts.subjective,
          objective: sectionMarks.objective / questionCounts.objective,
        },
      },
    });
  };

  const backButtonHandler = () => {
    navigate(`/create-assessment/step1/${projectId}`);
  };

  const toggleSubjectiveTooltip = () => {
    setSubjectiveTooltip(!subjectiveTooltip);
  };

  const toggleObjectiveTooltip = () => {
    setObjectiveTooltip(!objectiveTooltip);
  };
  const togglesetAiTooltip = () => {
    setAiTooltip(!aiTooltip);
  };

  return (
    <div className="">
      {/* <img  className="w-20 p-5" onClick={backButtonHandler}  src={backicon} alt='Back'/> */}
      <div className="flex justify-between items-center lg:w-7/12 md:w-9/12 w-11/12 mx-auto pt-4">
        <div className="flex items-center gap-20">
          <button
            onClick={backButtonHandler}
            className="group flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow-md hover:scale-105 active:scale-95 transition"
             >
            <IoMdArrowRoundBack className="text-xl group-hover:-translate-x-1 transition" />
            <span className="hidden md:block">Back</span>
          </button>
                             
        </div>
        <h3 className="md:text-lg text-sm text-[#36369f] font-semibold">
          Total Estimated Time:{" "}
          <span className="text-red-500 font-extrabold">
            {calculateTotalTime()} minutes
          </span>
        </h3>
      </div>
      <h2 className="lg:text-xl md:text-lg text-base text-[#36369f] font-bold lg:pt-14 md:pt-8 pt-4 text-center">
        Set Number of Questions and Marks for Each Selected Section:
      </h2>
      {isNextDisabled && errorMessage && (
        <div className="text-red-500 text-center mb-3">{errorMessage}</div>
      )}

      <div className="lg:w-9/12 w-11/12 mx-auto flex md:flex-row flex-col items-center justify-center gap-4">
        {sections.conversational_ai && (
          <div className="group rounded-lg border-2 bg-white md:w-1/3 w-11/12 mx-auto lg:px-4 px-1 pt-4 pb-6 
            hover:shadow-lg hover:shadow-[#36369f]/30 hover:-translate-y-1 hover:border-[#36369f] transition-all duration-300">
            <div className="flex items-center justify-start gap-3 px-2">
              <img
                src="/bot.png"
                alt="Conversational AI Questions"
                className="lg:w-14 w-10 lg:h-14 h-10"
                aria-label="image depicting conversational AI questions."
              />
              <div className="flex items-center justify-center">
                <h1 className="lg:text-base text-sm text-[#36369f] font-bold text-center">
                  Conversational AI Questions
                </h1>
                <FaInfoCircle
                  className="ml-2 text-blue-700  cursor-pointer"
                  onClick={togglesetAiTooltip}
                  aria-label="icon image - maximum number of questions not more than 10 and Total Marks should be a multiple of the number of questions selected."
                />
              </div>
            </div>

            {aiTooltip && (
              <div className="bg-gray-200 p-2 rounded md:text-sm text-xs text-black mb-2 text-justify">
                Maximum number of questions: 10
                <br />
                Total Marks should be a multiple of the number of questions
                selected.
              </div>
            )}

            <div className="mx-auto px-3 pt-5">
              <div className="flex pb-2 justify-between items-center lg:pr-8">
                <p className="w-[60%] font-bold text-[#36369f] lg:text-base text-sm">
                  No. of Questions:
                </p>
                <input
                  className="w-[40%] border-2 border-slate-400 font-bold text-xl rounded-lg text-center text-[#36369f]"
                  type="number"
                  min="3"
                  max="10"
                  value={questionCounts.conversational}
                  onChange={(e) =>
                    setQuestionCounts({
                      ...questionCounts,
                      conversational: +e.target.value,
                    })
                  }
                />
              </div>

              <div className="w-full flex justify-between items-center lg:pr-8">
                <p className="w-[60%] font-bold text-[#36369f] lg:text-base text-sm">
                  Total Marks:
                </p>
                <input
                  className="w-[40%] border-2 border-slate-400 font-bold text-xl rounded-lg text-center text-[#36369f]"
                  type="number"
                  min={questionCounts.conversational || 1}
                  value={sectionMarks.conversational}
                  onChange={(e) =>
                    setSectionMarks({
                      ...sectionMarks,
                      conversational: +e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
        )}
        {sections.subjective && (
         <div className="group rounded-lg border-2 bg-white md:w-1/3 w-11/12 mx-auto lg:px-4 px-1 pt-4 pb-6 
              hover:shadow-lg hover:shadow-[#36369f]/30 hover:border-[#36369f] hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-start gap-3 px-2">
              <img
                src="/assessment.png"
                alt="Subjective Questions"
                className="lg:w-14 w-10 lg:h-14 h-10"
                aria-label="image depicting subjective questions."
              />
              <div className="flex items-center justify-center">
                <h1 className="lg:text-base text-sm text-[#36369f] font-bold text-center">
                  Subjective Questions
                </h1>
                <FaInfoCircle
                  className="ml-2 text-blue-700  cursor-pointer"
                  onClick={toggleSubjectiveTooltip}
                  aria-label="icon image - maximum number of questions not more than 10 and Total Marks should be a multiple of the number of questions selected."
                />
              </div>
            </div>
            {subjectiveTooltip && (
              <div className="bg-gray-200 p-2 rounded md:text-sm text-xs text-black mb-2 text-justify ">
                Maximum number of questions: 10
                <br />
                Total Marks should be a multiple of the number of questions
                selected.
              </div>
            )}
            <div className="mx-auto px-3 pt-5">
              <div className="flex pb-2 justify-between items-center lg:pr-8">
                <p className="w-[60%] font-bold lg:text-base text-[#36369f] text-sm">
                  No. of Questions:
                </p>
                <input
                  className="w-[40%] border-2 border-slate-400 font-bold text-xl rounded-lg text-center text-[#36369f]"
                  type="number"
                  min="3"
                  max="10"
                  value={questionCounts.subjective}
                  onChange={(e) =>
                    setQuestionCounts({
                      ...questionCounts,
                      subjective: +e.target.value,
                    })
                  }
                />
              </div>

              <div className="w-full flex justify-between items-center lg:pr-8">
                <p className="w-[60%] font-bold lg:text-base text-[#36369f] text-sm">
                  Total Marks:
                </p>
                <input
                  className="w-[40%] border-2 border-slate-400 font-bold text-xl rounded-lg text-center text-[#36369f]"
                  type="number"
                  min={questionCounts.subjective || 1}
                  value={sectionMarks.subjective}
                  onChange={(e) =>
                    setSectionMarks({
                      ...sectionMarks,
                      subjective: +e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
        )}
        {sections.objective && (
          <div className="group rounded-lg border-2 bg-white md:w-1/3 w-11/12 mx-auto lg:px-4 px-1 pt-4 pb-6 
                hover:shadow-lg hover:shadow-[#36369f]/30 hover:border-[#36369f] hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-start gap-3 px-2">
              <img
                src="/objective.png"
                alt="Objective Questions"
                className="lg:w-14 w-10 lg:h-14 h-10"
                aria-label="image depicting objective questions."
              />
              <div className="flex items-center justify-center">
              <h1 className="lg:text-base text-sm font-bold text-[#36369f] text-center">
                  Objective Questions
                </h1>
                <FaInfoCircle
                  className="ml-2 text-blue-700  cursor-pointer"
                  onClick={toggleObjectiveTooltip}
                  aria-label="icon image - maximum number of questions not more than 20 and Total Marks should be a multiple of the number of questions selected."
                />
              </div>
            </div>
            {objectiveTooltip && (
              <div className="bg-gray-200 p-2 rounded md:text-sm text-xs text-black mb-2 text-justify ">
                Maximum number of questions: 20
                <br />
                Total Marks should be a multiple of the number of questions
                selected.
              </div>
            )}
            <div className="mx-auto px-3 pt-5">
              <div className="flex pb-2 justify-between items-center lg:pr-8">
                <p className="w-[60%] font-bold lg:text-base text-sm text-[#36369f]">
                  No. of Questions:</p>
                <input
                  className="w-[40%] border-2 border-slate-400 font-bold text-xl rounded-lg text-center text-[#36369f]"
                  type="number"
                  min="3"
                  max="20"
                  value={questionCounts.objective}
                  onChange={(e) =>
                    setQuestionCounts({
                      ...questionCounts,
                      objective: +e.target.value,
                    })
                  }
                />
              </div>

              <div className="w-full flex justify-between items-center lg:pr-8">
                <p className="w-[60%] font-bold lg:text-base text-sm text-[#36369f]">
                  Total Marks:
                </p>
                <input
                  className="w-[40%] border-2 border-slate-400 font-bold text-xl rounded-lg text-center text-[#36369f]"
                  type="number"
                  min={questionCounts.objective || 1}
                  value={sectionMarks.objective}
                  onChange={(e) =>
                    setSectionMarks({
                      ...sectionMarks,
                      objective: +e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>

     <div className="w-full py-12 flex justify-center">
  <button
    className={`px-8 py-3 rounded-xl font-semibold transition ${
      isNextDisabled
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:scale-105 shadow-lg"
    }`}
    onClick={handleNext}
    disabled={isNextDisabled}
  >
    Create Questions →
  </button>
</div>

    </div>
  );
};

export default CreateAssessmentPage2;
